import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtINR, fmtDate, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CurrencyInr, ArrowClockwise, XCircle, FilePdf, PaperPlaneTilt } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";

export default function InvoiceDetail() {
  const { id } = useParams();
  const { company } = useAuth();
  const [inv, setInv] = useState(null);
  const [payAmount, setPayAmount] = useState(0);
  const [method, setMethod] = useState("bank");
  const [reference, setReference] = useState("");
  const [useTds, setUseTds] = useState(false);
  const [tdsSection, setTdsSection] = useState("194J");
  const [tdsRate, setTdsRate] = useState(10);
  const [tdsSections, setTdsSections] = useState([]);
  const [emailTo, setEmailTo] = useState("");
  const [showEmail, setShowEmail] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/invoices/${id}`);
    setInv(data);
    setPayAmount(+(data.total - (data.amount_paid || 0)).toFixed(2));
    setEmailTo(data.customer_snapshot?.email || "");
  };
  useEffect(() => {
    load();
    api.get("/tds/sections").then((r) => setTdsSections(r.data)).catch(() => {});
  }, [id]);

  const onTdsSection = (code) => {
    setTdsSection(code);
    const s = tdsSections.find((x) => x.code === code);
    if (s) setTdsRate(s.rate);
  };

  if (!inv) return <div className="text-neutral-500">Loading…</div>;

  const generate = async () => {
    try {
      await api.post(`/einvoice/generate/${id}`);
      toast.success("IRN generated");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const cancel = async () => {
    if (!confirm("Cancel e-Invoice IRN?")) return;
    try {
      await api.post(`/einvoice/cancel/${id}`);
      toast.success("e-Invoice cancelled");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const downloadPdf = async () => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_no.replace(/\//g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF download failed"); }
  };
  const recordPayment = async (e) => {
    e.preventDefault();
    if (payAmount <= 0) return toast.error("Amount must be > 0");
    try {
      if (useTds) {
        const gross = Number(payAmount);
        const tds = +(gross * tdsRate / 100).toFixed(2);
        const net = +(gross - tds).toFixed(2);
        await api.post("/payments/tds", {
          invoice_id: id, amount_received: net, tds_amount: tds,
          tds_section: tdsSection, method, reference,
          payment_date: new Date().toISOString().slice(0, 10),
        });
        toast.success(`Payment recorded · TDS ₹${tds} deducted (${tdsSection})`);
      } else {
        await api.post("/payments", {
          invoice_id: id, amount: Number(payAmount), method, reference,
          payment_date: new Date().toISOString().slice(0, 10),
        });
        toast.success("Payment recorded");
      }
      setReference("");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const sendEmail = async () => {
    try {
      const { data } = await api.post(`/invoices/${id}/email`, { to: emailTo });
      toast.success(data.status === "sent" ? "Email sent" : "Email logged (Resend not configured)");
      setShowEmail(false);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const balance = +(inv.total - (inv.amount_paid || 0)).toFixed(2);

  return (
    <div className="space-y-5 lb-fade" data-testid="invoice-detail-page">
      <div className="flex items-center gap-3">
        <Link to="/app/invoices" className="text-neutral-500 hover:text-[#0A66C2]"><ArrowLeft size={16} /></Link>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Invoice</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight font-mono" data-testid="inv-no">{inv.invoice_no}</h1>
        </div>
        <span className={"lb-badge " + (
          inv.status === "paid" ? "lb-badge-success" :
          inv.status === "partial" ? "lb-badge-warning" : "lb-badge-danger"
        )}>{inv.status}</span>
        <button onClick={downloadPdf} className="lb-btn lb-btn-ghost lb-btn-sm" data-testid="pdf-download">
          <FilePdf size={14} /> PDF
        </button>
        <button onClick={() => setShowEmail(true)} className="lb-btn lb-btn-ghost lb-btn-sm" data-testid="email-invoice-btn">
          <PaperPlaneTilt size={14} /> Email
        </button>
      </div>

      {showEmail && (
        <div className="fixed inset-0 z-40 bg-black/50 grid place-items-center p-4" onClick={() => setShowEmail(false)}>
          <div className="lb-card p-5 w-full max-w-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="font-display font-bold text-lg mb-3">Email invoice</div>
            <label className="lb-label">To</label>
            <input className="lb-input" value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                   type="email" data-testid="email-to" placeholder="customer@company.com" />
            <div className="flex gap-2 mt-4 justify-end">
              <button className="lb-btn lb-btn-ghost" onClick={() => setShowEmail(false)}>Cancel</button>
              <button className="lb-btn" onClick={sendEmail} data-testid="email-send">
                <PaperPlaneTilt size={14} /> Send
              </button>
            </div>
            <p className="text-xs text-neutral-500 mt-3">
              If Resend API key isn&apos;t configured, this logs the email payload for testing.
            </p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Main invoice */}
        <div className="lg:col-span-2 lb-card p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4 pb-4 border-b border-neutral-200">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">From</div>
              <div className="font-semibold mt-1">{company?.name}</div>
              <div className="font-mono text-xs text-neutral-600">{company?.gstin}</div>
              <div className="text-xs text-neutral-500">{company?.address}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Bill to</div>
              <div className="font-semibold mt-1">{inv.customer_snapshot?.name}</div>
              <div className="font-mono text-xs text-neutral-600">{inv.customer_snapshot?.gstin || "B2C"}</div>
              <div className="text-xs text-neutral-500">{inv.customer_snapshot?.address}</div>
            </div>
            <div className="text-xs">
              <span className="text-neutral-500">Invoice date: </span><span className="font-mono">{fmtDate(inv.invoice_date)}</span>
            </div>
            <div className="text-xs">
              <span className="text-neutral-500">Due date: </span><span className="font-mono">{fmtDate(inv.due_date)}</span>
            </div>
          </div>

          <table className="lb-table">
            <thead>
              <tr>
                <th>Item</th><th>HSN</th><th className="text-right">Qty</th>
                <th className="text-right">Rate</th><th className="text-right">Amount</th>
                <th className="text-right">CGST</th><th className="text-right">SGST</th>
                <th className="text-right">IGST</th><th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {inv.lines.map((l, i) => (
                <tr key={i}>
                  <td>{l.name}</td>
                  <td className="font-mono text-xs">{l.hsn}</td>
                  <td className="num">{l.quantity}</td>
                  <td className="num">{fmtINR(l.price)}</td>
                  <td className="num">{fmtINR(l.amount)}</td>
                  <td className="num">{fmtINR(l.cgst)}</td>
                  <td className="num">{fmtINR(l.sgst)}</td>
                  <td className="num">{fmtINR(l.igst)}</td>
                  <td className="num font-semibold">{fmtINR(l.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end">
            <div className="w-72 font-mono space-y-1 text-sm">
              <Row l="Subtotal" v={inv.subtotal} />
              {inv.cgst > 0 && <Row l="CGST" v={inv.cgst} />}
              {inv.sgst > 0 && <Row l="SGST" v={inv.sgst} />}
              {inv.igst > 0 && <Row l="IGST" v={inv.igst} />}
              <div className="border-t border-neutral-300 pt-1 flex justify-between font-bold text-base">
                <span>Total</span><span data-testid="inv-total">{fmtINR(inv.total)}</span>
              </div>
              <Row l="Amount paid" v={inv.amount_paid || 0} />
              <div className="flex justify-between font-bold text-rose-600">
                <span>Balance</span><span data-testid="inv-balance">{fmtINR(balance)}</span>
              </div>
            </div>
          </div>

          {inv.notes && (
            <div className="border-t border-neutral-200 pt-3">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Notes</div>
              <div className="text-sm mt-1">{inv.notes}</div>
            </div>
          )}
        </div>

        {/* Side panel */}
        <div className="space-y-5">
          <div className="lb-card p-5" data-testid="einvoice-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold">e-Invoice (IRP)</div>
              <span className={"lb-badge " + (
                inv.einvoice_status === "generated" ? "lb-badge-info" :
                inv.einvoice_status === "cancelled" ? "lb-badge-danger" :
                inv.einvoice_status === "not_applicable" ? "" : "lb-badge-warning"
              )}>{inv.einvoice_status}</span>
            </div>
            {inv.qr_code && (
              <div className="mb-3">
                <img src={inv.qr_code} alt="QR" className="w-40 h-40 border border-neutral-200 p-1 bg-white" data-testid="einvoice-qr" />
              </div>
            )}
            {inv.irn && (
              <div className="space-y-2 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">IRN</div>
                  <div className="font-mono break-all mt-1" data-testid="inv-irn">{inv.irn}</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] uppercase text-neutral-500 font-semibold">Ack No.</div>
                    <div className="font-mono mt-1">{inv.ack_no}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-neutral-500 font-semibold">Ack Date</div>
                    <div className="font-mono mt-1">{fmtDate(inv.ack_date)}</div>
                  </div>
                </div>
              </div>
            )}
            <div className="flex gap-2 mt-4">
              {inv.is_b2b && inv.einvoice_status !== "generated" && (
                <button onClick={generate} className="lb-btn lb-btn-sm" data-testid="einvoice-generate">
                  <ArrowClockwise size={12} /> Generate IRN
                </button>
              )}
              {inv.einvoice_status === "generated" && (
                <button onClick={cancel} className="lb-btn lb-btn-ghost lb-btn-sm" data-testid="einvoice-cancel">
                  <XCircle size={12} /> Cancel IRN
                </button>
              )}
            </div>
          </div>

          {balance > 0 && (
            <form onSubmit={recordPayment} className="lb-card p-5 space-y-3" data-testid="payment-panel">
              <div className="flex items-center justify-between">
                <div className="font-display font-bold">Record payment</div>
                <label className="flex items-center gap-2 text-xs text-neutral-600">
                  <input type="checkbox" checked={useTds} onChange={(e) => setUseTds(e.target.checked)}
                         data-testid="pay-tds-toggle" />
                  Deduct TDS
                </label>
              </div>
              <div>
                <label className="lb-label">{useTds ? "Gross amount (before TDS)" : "Amount"}</label>
                <input type="number" step="0.01" className="lb-input font-mono" value={payAmount}
                       onChange={(e) => setPayAmount(e.target.value)} data-testid="pay-amount" />
              </div>
              {useTds && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="lb-label">TDS Section</label>
                    <select className="lb-select" value={tdsSection}
                            onChange={(e) => onTdsSection(e.target.value)} data-testid="tds-section">
                      {tdsSections.map((s) => (
                        <option key={s.code} value={s.code}>{s.code} · {s.rate}%</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="lb-label">Rate %</label>
                    <input type="number" step="0.01" className="lb-input font-mono text-right"
                           value={tdsRate} onChange={(e) => setTdsRate(Number(e.target.value))} />
                  </div>
                  <div className="col-span-2 bg-neutral-50 border border-neutral-200 p-2 text-xs font-mono">
                    Gross ₹{Number(payAmount || 0).toFixed(2)} − TDS ₹{(Number(payAmount || 0) * tdsRate / 100).toFixed(2)}
                    = <b>Net ₹{(Number(payAmount || 0) * (1 - tdsRate / 100)).toFixed(2)}</b>
                  </div>
                </div>
              )}
              <div>
                <label className="lb-label">Method</label>
                <select className="lb-select" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="pay-method">
                  <option value="bank">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="razorpay">Razorpay</option>
                </select>
              </div>
              <div>
                <label className="lb-label">Reference (UTR / Txn #)</label>
                <input className="lb-input font-mono" value={reference} onChange={(e) => setReference(e.target.value)}
                       data-testid="pay-ref" />
              </div>
              <button className="lb-btn w-full" data-testid="pay-submit">
                <CurrencyInr size={14} /> Record Payment
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ l, v }) {
  return (
    <div className="flex justify-between">
      <span className="text-neutral-600">{l}</span>
      <span>{fmtINR(v)}</span>
    </div>
  );
}
